export interface BoardExtent {
  height: number;
  width: number;
}

export interface BoardViewportMeasurements {
  clientHeight: number;
  clientWidth: number;
  scrollHeight: number;
  scrollLeft: number;
  scrollTop: number;
  scrollWidth: number;
}

export interface BoardScrollDelta {
  deltaX: number;
  deltaY: number;
}

export const boardExtentPadding = 20;
export const edgeExpansionStep = 1000;
export const edgeScrollThreshold = 350;

export function getMinimumBoardExtent(
  contentExtent: BoardExtent,
  viewportExtent: BoardExtent
): BoardExtent {
  return {
    height: Math.max(contentExtent.height, viewportExtent.height + boardExtentPadding),
    width: Math.max(contentExtent.width, viewportExtent.width + boardExtentPadding),
  };
}

export function growBoardExtentToMinimum(
  currentExtent: BoardExtent,
  minimumExtent: BoardExtent
): BoardExtent {
  if (currentExtent.height >= minimumExtent.height && currentExtent.width >= minimumExtent.width) {
    return currentExtent;
  }

  return {
    height: Math.max(currentExtent.height, minimumExtent.height),
    width: Math.max(currentExtent.width, minimumExtent.width),
  };
}

export function expandBoardExtentNearScrollEdges(
  currentExtent: BoardExtent,
  measurements: BoardViewportMeasurements
): BoardExtent {
  const isNearBottom =
    Math.round(measurements.scrollHeight - measurements.scrollTop) <=
    Math.round(measurements.clientHeight) + edgeScrollThreshold;
  const isNearRight =
    Math.round(measurements.clientWidth + measurements.scrollLeft) >=
    Math.round(measurements.scrollWidth) - edgeScrollThreshold;

  if (!isNearBottom && !isNearRight) {
    return currentExtent;
  }

  return {
    height: isNearBottom ? currentExtent.height + edgeExpansionStep : currentExtent.height,
    width: isNearRight ? currentExtent.width + edgeExpansionStep : currentExtent.width,
  };
}

export function expandBoardExtentForProjectedScroll(
  currentExtent: BoardExtent,
  measurements: BoardViewportMeasurements,
  delta: BoardScrollDelta
): BoardExtent {
  const projectedBottom =
    measurements.scrollTop + measurements.clientHeight + Math.max(0, delta.deltaY);
  const projectedRight =
    measurements.scrollLeft + measurements.clientWidth + Math.max(0, delta.deltaX);
  const bottomGap = measurements.scrollHeight - projectedBottom;
  const rightGap = measurements.scrollWidth - projectedRight;
  const expandHeightBy =
    delta.deltaY > 0 && bottomGap <= edgeScrollThreshold
      ? getExpansionAmount(edgeScrollThreshold - bottomGap)
      : 0;
  const expandWidthBy =
    delta.deltaX > 0 && rightGap <= edgeScrollThreshold
      ? getExpansionAmount(edgeScrollThreshold - rightGap)
      : 0;

  if (expandHeightBy === 0 && expandWidthBy === 0) {
    return currentExtent;
  }

  return {
    height: currentExtent.height + expandHeightBy,
    width: currentExtent.width + expandWidthBy,
  };
}

function getExpansionAmount(requiredSpace: number) {
  return Math.max(
    edgeExpansionStep,
    Math.ceil(requiredSpace / edgeExpansionStep) * edgeExpansionStep
  );
}
